import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SideNavigator } from '../components/SideNavigator';
import { useStore } from '../../../store';
import Heading from '../../../generic-components/Heading';
import DigitsContainer from '../../../generic-components/DigitsContainer';
import { DigitsNumAccordingToType, MfaType } from '../../../generic-components/MFA/types';
import { useValidateMfa } from '../hooks/useValidateMfa';
import { VIEWS_MAP } from '../../../router/constants';
import RadioButton from '../../../generic-components/RadioButton';
import Button from '../../../generic-components/Button';
import { useResendOtpCode } from '../hooks/useResendOtpCode';
import { OnboardingSteps } from '../consts';
import { useCountdownTimer } from '../../../hooks/utilities/useCountdownTimer';
import { convertToTimeFormat } from '../../../helpers/currency.helper';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { LOGIN_ERRORS } from '../../../flows/login/constants/messages';

export const VerifyPhone = () => {
  const [resendType, setResendType] = useState<string>(MfaType.SMS);
  const [isResendCode, setIsResendCode] = useState<boolean>(false);
  const mfaData = useStore((state) => state.mfaData);
  const setMfaData = useStore((state) => state.setMfaData);
  const obData = useStore((state) => state.obData);
  const setObData = useStore((state) => state.setObData);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { startTimer, timeLeft } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });
  const numberOfDigits = useMemo(
    () => (mfaData?.mfaType === MfaType.SMS ? DigitsNumAccordingToType.SMS : DigitsNumAccordingToType.VOICE),
    [mfaData],
  );
  const {
    mutateAsync: asyncValidateMfa,
    isLoading,
    error,
    reset: resetValidateMfa,
  } = useValidateMfa(() => {
    // TODO: Implement error
  });
  const { mutateAsync: asyncResendCode, isLoading: isLoadingResend } = useResendOtpCode();

  const handleMfaCode = async (mfaCode: string) => {
    if (mfaData?.customerId && mfaData.authorizedExternalId && mfaData.mfaType) {
      const res = await asyncValidateMfa({
        customerId: mfaData?.customerId,
        authorizedUserId: mfaData?.authorizedExternalId,
        mfaType: mfaData?.mfaType,
        answer: mfaCode,
      });
      setObData({ ...obData, accessToken: res.accessToken });
      navigate(`/${VIEWS_MAP.ACCOUNTANT_ONBOARDING.START}/${VIEWS_MAP.ACCOUNTANT_ONBOARDING.CREATE_PASSWORD}`);
    }
  };

  const handleResend = async () => {
    if (mfaData?.authorizedExternalId && mfaData.customerId) {
      const resMfaData = await asyncResendCode({
        customerId: mfaData?.customerId,
        authorizedUserId: mfaData?.authorizedExternalId,
        mfaType: resendType,
      });
      resetValidateMfa();
      setMfaData(resMfaData);
      setIsResendCode(false);
      startTimer();
    }
  };

  const dynamicTitle = useMemo(() => {
    if (resendType === MfaType.SMS) {
      return 'Text me';
    }
    if (resendType === MfaType.VOICE) {
      return 'Call me';
    }
    return 'Send';
  }, [resendType]);

  useEffect(() => {
    trackMixPanel('Page view', { pageName: 'Accountant MFA' });
    setObData({ ...obData, lastStepVisit: OnboardingSteps.verifyPhone });
  }, []);

  return mfaData ? (
    <div className='flex items-center grow h-full'>
      <SideNavigator />
      <div className='flex flex-col justify-center grow h-full bg-bw00 fadeIn'>
        {!isResendCode && (
          <div className='flex flex-col justify-center grow max-w-[485px] pl-[120px]'>
            <Heading
              title={`${mfaData.authorizedUserFullName.split(' ')[0]}${t('ACCOUNTANT_OB.VERIFY_PHONE.HEADING')}`}
              className='font-metricRMedium text-bw08 mb-[6px] leading-[34px] text-[28px] text-left'
            />
            <Heading
              title={`${t('ACCOUNTANT_OB.VERIFY_PHONE.SUB_HEADING')}${mfaData.mfaDestination}`}
              className='font-metricR text-bw08 text-[16px] mb-[24px] text-left'
            />
            <DigitsContainer
              numOfDigits={numberOfDigits}
              onFulfill={(mfaCode) => handleMfaCode(mfaCode)}
              isError={!!error}
              isLoading={isLoading}
            />
            {error && (
              <div className='font-metricR text-red02 text-sm text-center mb-4'>{t(LOGIN_ERRORS.BAD_REQUEST)}</div>
            )}
            {!timeLeft && (
              <Button
                onClick={() => setIsResendCode(true)}
                className='mt-[14px] w-fit font-metricR text-action03 hover:text-action04'
                title={t('ACCOUNTANT_OB.VERIFY_PHONE.CTA')}
                id='initResend'
              />
            )}
            {timeLeft && (
              <div className='flex items-center gap-[4px]'>
                <Heading
                  title={t('ACCOUNTANT_OB.VERIFY_PHONE.RESEND_TIMER')}
                  className='font-metricR text-[14px] text-bw07'
                />
                <Heading title={convertToTimeFormat(timeLeft)} className='font-metricR text-[14px] text-action03' />
              </div>
            )}
          </div>
        )}
        {isResendCode && (
          <div className='flex flex-col justify-center grow max-w-[485px] pl-[120px] fadeIn'>
            <Heading
              title={t('ACCOUNTANT_OB.VERIFY_PHONE.RESEND')}
              className='font-metricRMedium text-bw08 mb-[6px] leading-[34px] text-[28px] text-left'
            />
            <Heading
              title={t('ACCOUNTANT_OB.VERIFY_PHONE.RESEND_SUB_HEADER')}
              className='font-metricR text-bw07 text-[16px] mb-[24px] text-left'
            />
            <div className='flex flex-col gap-[8px]'>
              <RadioButton
                id={MfaType.SMS}
                title={t('ACCOUNTANT_OB.VERIFY_PHONE.SMS')}
                checked={resendType === MfaType.SMS}
                onClick={(id) => setResendType(id)}
              />
              <RadioButton
                id={MfaType.VOICE}
                title={t('ACCOUNTANT_OB.VERIFY_PHONE.PHONE')}
                checked={resendType === MfaType.VOICE}
                onClick={(id) => setResendType(id)}
              />
            </div>
            <Button
              isLoading={isLoadingResend}
              disable={isLoadingResend}
              onClick={handleResend}
              className='w-full bg-action03 hover:bg-action04 mt-[40px] font-metricRMedium text-bw00 py-[17px] px-[60px] rounded-[4px]'
              title={dynamicTitle}
              id='resendCode'
            />
            <Button
              disable={isLoadingResend}
              onClick={() => setIsResendCode(false)}
              className='mt-[14px] font-metricR text-bw07 hover:text-bw08 underline text-[14px]'
              title={t('ACCOUNTANT_OB.VERIFY_PHONE.RESEND_BACK')}
              id='back'
            />
          </div>
        )}
      </div>
    </div>
  ) : null;
};
